import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Column,
  GridContainer,
  ModalDialogBody,
  ModalDialogButtons,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
  Row,
} from '@vp/swan';
import { AddressForm } from '@vp/addressform';
import { useTranslations } from '@vp/om-translations';
import addressPropType from './AddressConstants';
import useSiteData from '../../../hooks/useSiteData';

export const AddressUpdateForm = ({ address, closeModal, saveAddress }) => {
  const [shippingAddressUpdate] = useTranslations(['shippingAddressUpdate']);

  const {
    locale,
    addressAutocompleteConfig,
    shippingAddressAutocompleteEnabled,
    loqateAPIKey,
    shippingAddressValidationEnabled,
  } = useSiteData();

  // Once the order has been placed, we do not allow customers
  // to edit the country of their address.  We need to lock
  // in the allowable countries on this form to only be the current address country
  const allowedCountry = address.country;
  const validCountries = [allowedCountry];

  let externalValidationConfig = {};
  if (shippingAddressValidationEnabled) {
    externalValidationConfig = {
      credentials: loqateAPIKey,
      allowedCountries: validCountries,
    };
  }

  let autoCompleteConfig = { provider: 'NONE' };
  if (shippingAddressAutocompleteEnabled) {
    autoCompleteConfig = {
      credentials: loqateAPIKey,
      allowedCountries: validCountries,
      provider: addressAutocompleteConfig.provider,
    };
  }

  const fieldConfigs = {};

  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false);

  const submitButtonConfig = {
    disabled: submitButtonDisabled,
    setDisabled: setSubmitButtonDisabled,
  };

  const addressFormRef = useRef();

  const triggerSaveOnAddressForm = () => {
    if (addressFormRef && addressFormRef.current) {
      addressFormRef.current.submit();
    }
  };

  return (
    <>
      <ModalDialogHeader>
        <ModalDialogTitle>
          {shippingAddressUpdate.editYourShippingAddress}
        </ModalDialogTitle>
      </ModalDialogHeader>
      <ModalDialogBody>
        <AddressForm
          initializationConfig={{
            locale,
            initialAddress: address,
            selectableCountries: validCountries,
            autoCompleteConfig,
            externalValidationConfig,
            fieldConfigs,
            submitButtonConfig,
            forbidPoBoxCountries: ['US', 'NO'],
          }}
          save={newAddress => {
            saveAddress(newAddress, address);
          }}
          data-testid="address-form"
          formRef={addressFormRef}
        />
      </ModalDialogBody>
      <ModalDialogFooter pinned>
        <ModalDialogButtons>
          <GridContainer>
            <Row>
              <Column span={6}>
                <Button
                  skin="secondary"
                  size="mini"
                  width="full-width"
                  m={2}
                  onClick={closeModal}
                >
                  {shippingAddressUpdate.cancel}
                </Button>
              </Column>
              <Column span={6}>
                <Button
                  skin="primary"
                  size="mini"
                  width="full-width"
                  m={2}
                  onClick={triggerSaveOnAddressForm}
                  disabled={submitButtonDisabled}
                >
                  {shippingAddressUpdate.update}
                </Button>
              </Column>
            </Row>
          </GridContainer>
        </ModalDialogButtons>
      </ModalDialogFooter>
    </>
  );
};

AddressUpdateForm.propTypes = {
  address: PropTypes.exact(addressPropType),
  closeModal: PropTypes.func.isRequired,
  saveAddress: PropTypes.func.isRequired,
};
